'use client'

import { useTranslation } from '@/i18n/client'
import { UserStatusDot } from '@/components/presence/user-status-dot'

/**
 * DonorAvatar (R91, 2026-05-19)
 *
 * Pilot-Foto mit Donor-Ring je nach Tier-Stufe + optional Name-Glow.
 * Plus optional ∞-Symbol-Badge oder Tage-Counter rechts unten.
 *
 * Stephan-Spec (5 Stufen):
 *   NONE              = kein Ring (kein Donor / abgelaufen)
 *   SUPPORTER         = 10–19,99 € einmal — blau statisch, 365 Tage Reset
 *   UNTERSTUETZER     = 20–49,99 € einmal — gold dünn statisch, 365 Tage Reset
 *   FOERDERER         = 50+ € einmal      — gold dick + Glow, ∞ dauerhaft
 *   SUPPORTER_MONTHLY = 5+ €/Mo Abo        — blau + drehender Gold, ∞ solange Abo
 *   GOENNER_MONTHLY   = 10+ €/Mo Abo       — gold+grün + drehender Gold, ∞ solange Abo
 *
 * Zeitlogik (Backend): jede Einmalspende RESET auf 365 Tage ab Spendendatum
 * (kein Stacking). Monatsabo = ∞ solange aktiv.
 */

export type DonorTier =
  | 'NONE'
  | 'SUPPORTER'
  | 'UNTERSTUETZER'
  | 'FOERDERER'
  | 'SUPPORTER_MONTHLY'
  | 'GOENNER_MONTHLY'
  // Legacy-Tier-Namen aus altem Schema (werden auf neue gemappt)
  | 'SUPPORTER_BLUE'
  | 'GOENNER_ONETIME'

interface TierStyle {
  ring: string
  ring2?: string
  ringWidth: number
  glow: number
  pulse: boolean
  spin: boolean
  forever: boolean
  nameGlow: string
  /** i18n-Key fuer den Hover-Tooltip / aria-label */
  labelKey: string
}

const TIER_STYLES: Partial<Record<DonorTier, TierStyle>> = {
  SUPPORTER: {
    ring: '#60a5fa',
    ringWidth: 2,
    glow: 8,
    pulse: false,
    spin: false,
    forever: false,
    nameGlow: '0 0 12px rgba(96, 165, 250, 0.40)',
    labelKey: 'donate.avatarSupporterLabel',
  },
  UNTERSTUETZER: {
    ring: '#f2c94e',
    ringWidth: 2,
    glow: 10,
    pulse: false,
    spin: false,
    forever: false,
    nameGlow: '0 0 14px rgba(242, 201, 78, 0.45)',
    labelKey: 'donate.avatarUnterstuetzerLabel',
  },
  FOERDERER: {
    ring: '#f2c94e',
    ringWidth: 4,
    glow: 18,
    pulse: true,
    spin: false,
    forever: false, // R91-Korrektur: Förderer = 365 Tage Reset, kein ∞ (∞ nur für Monthly-Abos)
    nameGlow: '0 0 18px rgba(242, 201, 78, 0.65)',
    labelKey: 'donate.avatarFoerdererLabel',
  },
  SUPPORTER_MONTHLY: {
    ring: '#60a5fa',
    ringWidth: 3,
    glow: 12,
    pulse: false,
    spin: true,
    forever: true,
    nameGlow: '0 0 14px rgba(96, 165, 250, 0.50), 0 0 22px rgba(242, 201, 78, 0.25)',
    labelKey: 'donate.avatarSupporterMonthlyLabel',
  },
  GOENNER_MONTHLY: {
    ring: '#f2c94e',
    ring2: '#34d399',
    ringWidth: 4,
    glow: 18,
    pulse: false,
    spin: true,
    forever: true,
    nameGlow: '0 0 18px rgba(242, 201, 78, 0.65), 0 0 26px rgba(52, 211, 153, 0.45)',
    labelKey: 'donate.avatarGoennerMonthlyLabel',
  },
}

// Legacy-Tier-Mapping
const LEGACY_MAP: Record<string, DonorTier> = {
  SUPPORTER_BLUE: 'SUPPORTER',
  GOENNER_ONETIME: 'FOERDERER',
}

function normalizeTier(t: DonorTier | string | null | undefined): DonorTier {
  if (!t) return 'NONE'
  const s = String(t).toUpperCase()
  if (LEGACY_MAP[s]) return LEGACY_MAP[s]
  if (s in TIER_STYLES || s === 'NONE') return s as DonorTier
  return 'NONE'
}

interface DonorAvatarProps {
  src?: string | null
  name: string | null
  donorTier?: DonorTier | string | null
  /** Verbleibende Tage (nur SUPPORTER + UNTERSTUETZER) */
  daysLeft?: number | null
  size?: number
  className?: string
  title?: string
  /** Soll ∞ / Days-Counter Overlay angezeigt werden? Default true */
  showBadge?: boolean
  /** Optional: statt Initial-Fallback einen der 5 inline PilotAvatare zeigen
   *  (für Donate-Page-Beispiele). Index 0-4. */
  exampleAvatarIndex?: number
  /** R100.60: Online-Status-Punkt oben rechts. Erfordert userId UND
   *  showPresence — wird in öffentlichen Komponenten weggelassen, sodass
   *  der Status nur im eingeloggten Bereich auftaucht. */
  userId?: string | null
  showPresence?: boolean
}

export function DonorAvatar({
  src, name, donorTier, daysLeft, size = 48, className = '', title, showBadge = true, exampleAvatarIndex,
  userId, showPresence = false,
}: DonorAvatarProps) {
  const { t } = useTranslation()
  const tier = normalizeTier(donorTier)
  const style = TIER_STYLES[tier]
  // R103.23 (T-Day, Stephan-Spec): Anfangsbuchstabe Vorname + Nachname in
  // Versalien, konsistent über alle Seiten (Wertung-Top-10, Kommentare,
  // Public-Flight-Hero, Pilot-Hero-Card). Bei nur einem Namensteil:
  // erster Buchstabe.
  const initial = (() => {
    const parts = (name || 'P').trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return parts[0]?.[0]?.toUpperCase() || 'P'
  })()
  const tooltip = title ?? (style?.labelKey ? t(style.labelKey) : null) ?? name ?? ''

  // Dot-Größe etwa 22 % der Avatar-Kantenlänge, min 8 px, max 14 px
  const dotSize = Math.max(8, Math.min(14, Math.round(size * 0.22)))
  const presenceDot = showPresence && userId ? (
    <span
      className="absolute"
      style={{ top: -2, right: -2, zIndex: 2 }}
    >
      <UserStatusDot userId={userId} size={dotSize} />
    </span>
  ) : null

  // Ohne Ring: einfaches Avatar
  if (!style) {
    return (
      <div
        className={`shrink-0 relative rounded-2xl overflow-visible ${className}`}
        style={{ width: size, height: size }}
        title={tooltip}
      >
        <div
          className="w-full h-full rounded-2xl overflow-hidden"
          style={{
            background: 'var(--award-gold-soft)',
            border: '1px solid color-mix(in oklch, var(--award-gold) 25%, transparent)',
          }}
        >
          <AvatarContent src={src} name={name} initial={initial} size={size} exampleAvatarIndex={exampleAvatarIndex} />
        </div>
        {presenceDot}
      </div>
    )
  }

  // Mit Donor-Ring
  const ringBackground = style.ring2
    ? `conic-gradient(from 0deg, ${style.ring}, ${style.ring2}, ${style.ring})`
    : style.ring
  // Bei pulsenden Tiers übernimmt die CSS-Animation den box-shadow
  const ringShadow = style.pulse
    ? undefined
    : `0 0 ${style.glow}px ${style.ring}${style.ring2 ? `, 0 0 ${style.glow * 0.6}px ${style.ring2}` : ''}`

  const classes = [
    'shrink-0 relative',
    style.pulse ? 'fgl-donor-pulse' : '',
    style.spin ? 'fgl-donor-spin' : '',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div
      className={classes}
      style={{
        width: size, height: size,
        padding: style.ringWidth,
        borderRadius: `${Math.round(size * 0.32)}px`,
        background: ringBackground,
        ...(ringShadow ? { boxShadow: ringShadow } : {}),
      }}
      title={tooltip}
      aria-label={tooltip}
    >
      <div
        className="w-full h-full overflow-hidden"
        style={{
          borderRadius: `${Math.round(size * 0.27)}px`,
          background: 'var(--award-gold-soft)',
        }}
      >
        <AvatarContent src={src} name={name} initial={initial} size={size} exampleAvatarIndex={exampleAvatarIndex} />
      </div>

      {/* ∞-Badge (dauerhaft) ODER Tage-Counter (365-Reset-Status) */}
      {showBadge && style.forever && (
        <span className="fgl-donor-infinity" title={t('donate.avatarForeverTooltip')}>∞</span>
      )}
      {showBadge && !style.forever && daysLeft != null && daysLeft > 0 && (
        <span className="fgl-donor-counter" title={t('donate.avatarDaysLeftTooltip', { days: daysLeft })}>
          {daysLeft}{t('donate.avatarDaysSuffix')}
        </span>
      )}

      {/* R100.60: Online-Status-Punkt nur sichtbar wenn showPresence + userId */}
      {presenceDot}
    </div>
  )
}

function AvatarContent({ src, name, initial, size, exampleAvatarIndex }: { src?: string | null; name: string | null; initial: string; size: number; exampleAvatarIndex?: number }) {
  const { t } = useTranslation()
  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={src} alt={name || t('donate.avatarPilotFallback')}
        className="w-full h-full object-cover"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
      />
    )
  }
  // R91.1: für Donate-Page-Beispiele einen der 5 illustrierten Pilot-Avatare
  if (typeof exampleAvatarIndex === 'number') {
    const { PilotAvatarByIndex } = require('./pilot-avatar-set') as typeof import('./pilot-avatar-set')
    return (
      <div className="w-full h-full">
        <PilotAvatarByIndex index={exampleAvatarIndex} size={size} className="w-full h-full" />
      </div>
    )
  }
  return (
    <div className="w-full h-full flex items-center justify-center font-black"
         style={{ color: 'var(--award-gold)', fontSize: Math.round(size * 0.42) }}>
      {initial}
    </div>
  )
}

/** Text-Shadow CSS-Wert für Name-Glow je Tier. */
export function donorNameGlow(donorTier: DonorTier | string | null | undefined): string | undefined {
  const tier = normalizeTier(donorTier)
  return TIER_STYLES[tier]?.nameGlow
}

/** Ist dieser User ein „echter" Donator? */
export function isDonor(donorTier: DonorTier | string | null | undefined): boolean {
  return normalizeTier(donorTier) !== 'NONE'
}

/** Hat dieser Tier dauerhaften ∞-Status? R91-Korrektur: nur Monthly-Abos. */
export function isDonorForever(donorTier: DonorTier | string | null | undefined): boolean {
  const t = normalizeTier(donorTier)
  return t === 'SUPPORTER_MONTHLY' || t === 'GOENNER_MONTHLY'
}

/** Verbleibende Tage bis Ring abläuft (nur SUPPORTER + UNTERSTUETZER). */
export function daysUntilExpiry(expiry: Date | string | null | undefined): number | null {
  if (!expiry) return null
  const d = typeof expiry === 'string' ? new Date(expiry) : expiry
  const ms = d.getTime() - Date.now()
  if (ms <= 0) return 0
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}
